import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Plus,
  Building2,
  DollarSign,
  Edit,
  Trash2,
  Search,
  FileText,
  Calculator,
  ShoppingCart,
  Eye,
  Check,
  X,
  Tv,
  Radio,
  Film,
  Sparkles,
  Share2,
  Newspaper,
  Monitor,
  Save,
  Download,
  Send,
  Mail,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import * as api from '../services/api';
import EmpresaSearchSelect from './EmpresaSearchSelect';
import { formatDatePR } from '../utils/dateUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Default service catalog based on the Excel price list
const defaultServiceCatalog = [
  // Creative Production
  { id: 'cp1', category: 'Creative Production', name: 'Design and Production (Video, Audio, Editing)', detail: 'Video, audio, Images, Graphics, Free Copyright music, etc.', format: 'Digital', baseRate: 75, unit: 'hour', defaultHours: 8 },
  { id: 'cp2', category: 'Creative Production', name: 'Raw Studio Productions (Green Screen)', detail: 'Green screen studio session', format: 'Digital', baseRate: 275, unit: 'session', defaultHours: 1 },
  { id: 'cp3', category: 'Creative Production', name: 'Stage Designs', detail: 'Custom stage design', format: 'Digital', baseRate: 40, unit: 'hour', defaultHours: 1 },
  { id: 'cp4', category: 'Creative Production', name: 'Landing Page', detail: 'Web landing page design', format: 'Digital', baseRate: 80, unit: 'page', defaultHours: 1 },
  { id: 'cp5', category: 'Creative Production', name: 'Ad Voiceover (Talent)', detail: 'Professional voice talent', format: 'Human', baseRate: 200, unit: 'ad', defaultHours: 1 },
  { id: 'cp6', category: 'Creative Production', name: 'Ad On Cam (Talent)', detail: 'On-camera talent', format: 'Human', baseRate: 200, unit: 'ad', defaultHours: 1 },
  // AI Production
  { id: 'ai1', category: 'AI Production', name: 'AI Video (30 Seconds)', detail: 'AI-generated video content', format: 'AI', baseRate: 125, unit: 'video', defaultHours: 1 },
  { id: 'ai2', category: 'AI Production', name: 'AI Avatar Video (30 Seconds)', detail: 'AI avatar presenter video', format: 'AI', baseRate: 175, unit: 'video', defaultHours: 1 },
  { id: 'ai3', category: 'AI Production', name: 'AI Voiceover (30 Seconds)', detail: 'AI-generated voiceover', format: 'AI', baseRate: 125, unit: 'voiceover', defaultHours: 1 },
  // Social Media
  { id: 'sm1', category: 'Social Media', name: 'Daily Posts (Art + Copy)', detail: 'Stories and photos with copy', format: 'Digital', baseRate: 25, unit: 'post', defaultDays: 7 },
  { id: 'sm2', category: 'Social Media', name: 'Daily Posts (AI Videos)', detail: 'AI video content posts', format: 'AI', baseRate: 45, unit: 'post', defaultDays: 7 },
  // Press
  { id: 'pr1', category: 'Press', name: 'Press Release on Noticel', detail: '377K Subscribers', format: 'News', baseRate: 600, unit: 'release', defaultHours: 1 },
  // Radio
  { id: 'ra1', category: 'Radio', name: '30-Second Radio Spots', detail: 'Radio advertisement', format: 'Audio', baseRate: 68.75, unit: 'spot', defaultDays: 8 },
  // Traditional Television
  { id: 'tv1', category: 'Traditional TV', name: '30 Seconds Ad - América Tevé PR', detail: '1.2M Subscribers', format: 'TV Commercial', baseRate: 62.50, unit: 'spot', defaultDays: 8 },
  // Digital TV
  { id: 'dtv1', category: 'Digital TV', name: 'Auspicios Conciertos Virtuales Plata', detail: 'Silver sponsorship', format: 'Live Program', baseRate: 250, unit: 'event', mcStaff: 25 },
  { id: 'dtv2', category: 'Digital TV', name: 'Auspicios Conciertos Virtuales Oro', detail: 'Gold sponsorship', format: 'Live Program', baseRate: 500, unit: 'event', mcStaff: 25 },
  { id: 'dtv3', category: 'Digital TV', name: 'Auspicios Conciertos Virtuales Platino', detail: 'Platinum sponsorship', format: 'Live Program', baseRate: 750, unit: 'event', mcStaff: 25 },
  { id: 'dtv4', category: 'Digital TV', name: 'Program (4 weeks)', detail: 'Full program production', format: 'Live Program', baseRate: 850, unit: 'program', defaultWeeks: 4, mcStaff: 50 },
  { id: 'dtv5', category: 'Digital TV', name: 'PR Live Promotions', detail: 'Live promotional event', format: 'Live Program', baseRate: 500, unit: 'event', laborPercent: 58.33, mcStaff: 25 },
  { id: 'dtv6', category: 'Digital TV', name: 'PR Live Promotions Modelo', detail: 'Model for live event', format: 'Live Program', baseRate: 250, unit: 'model', laborPercent: 60 },
  { id: 'dtv7', category: 'Digital TV', name: 'PR Live Promotions Cámara Adicional', detail: 'Additional camera setup', format: 'Live Program', baseRate: 150, unit: 'camera', mcStaff: 25 },
];

const getIconForCategory = (category) => {
  switch (category) {
    case 'Creative Production': return Film;
    case 'AI Production': return Sparkles;
    case 'Social Media': return Share2;
    case 'Press': return Newspaper;
    case 'Radio': return Radio;
    case 'Traditional TV': return Tv;
    case 'Digital TV': return Monitor;
    default: return FileText;
  }
};

const EvaluacionesPage = ({ currentUser, globalSearch = '' }) => {
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('cotizador');
  
  // Service catalog state (editable)
  const [serviceCatalog, setServiceCatalog] = useState(() => {
    const saved = localStorage.getItem('serviceCatalog');
    return saved ? JSON.parse(saved) : defaultServiceCatalog;
  });
  
  // Cotizador state
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [assignedUser, setAssignedUser] = useState(''); // User assigned to this quote
  
  // Service CRUD modals
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: '', detail: '', category: 'Creative Production', format: 'Digital',
    baseRate: 0, unit: 'hour', defaultHours: 1, defaultDays: 1, defaultWeeks: 1
  });
  
  // Quote detail/edit modal
  const [showQuoteDetailModal, setShowQuoteDetailModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(false);
  
  // Send quote modal
  const [showSendQuoteModal, setShowSendQuoteModal] = useState(false);
  const [sendQuoteData, setSendQuoteData] = useState({
    empresa_id: '',
    contact_id: '',
    contact_email: '',
    message: ''
  });
  const [sendEmpresaContacts, setSendEmpresaContacts] = useState([]);
  
  // Quote configuration modal
  const [showQuoteConfigModal, setShowQuoteConfigModal] = useState(false);
  const [quoteConfig, setQuoteConfig] = useState({
    template: 'professional', // professional, minimal, corporate, colorful
    global_note: '',
    show_logo: true,
    show_company_info: true,
    logo_position: 'left', // left, center, right
    primary_color: '#5A5A5A',
    accent_color: '#D4A843',
    balance_due_color: '#E6F5FF',
    footer_text: 'Infinito Televisión - Producción y Marketing Digital',
    terms_text: '',
    payment_info: '',
    show_taxes: true,
    show_amount_paid: true,
    header_style: 'clean' // clean, boxed, gradient
  });
  
  // Available templates
  const templates = [
    { 
      id: 'professional', 
      name: 'Profesional', 
      description: 'Diseño limpio similar al ejemplo',
      preview: '📄',
      defaults: { primary_color: '#5A5A5A', accent_color: '#D4A843', balance_due_color: '#E6F5FF', header_style: 'clean' }
    },
    { 
      id: 'minimal', 
      name: 'Minimalista', 
      description: 'Diseño simple y moderno',
      preview: '📃',
      defaults: { primary_color: '#333333', accent_color: '#666666', balance_due_color: '#F5F5F5', header_style: 'clean' }
    },
    { 
      id: 'corporate', 
      name: 'Corporativo', 
      description: 'Diseño formal con encabezado',
      preview: '📋',
      defaults: { primary_color: '#1a365d', accent_color: '#2b6cb0', balance_due_color: '#ebf8ff', header_style: 'boxed' }
    },
    { 
      id: 'colorful', 
      name: 'Colorido', 
      description: 'Diseño vibrante con colores',
      preview: '🎨',
      defaults: { primary_color: '#7c3aed', accent_color: '#f59e0b', balance_due_color: '#fef3c7', header_style: 'gradient' }
    }
  ];
  
  // Company info for professional quotes
  const companyInfo = {
    name: 'Infinito Televisión',
    website: 'www.infinitotelevision.com',
    email: 'infinitotelevisionpr@gmail.com',
    phone: '(787) 210-5788',
    logo: '/logo_infinito.png'
  };
  
  // Load logo as base64 for PDF
  const [logoBase64, setLogoBase64] = useState(null);
  
  useEffect(() => {
    // Load logo for PDF
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo_infinito.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        console.log('Could not load logo for PDF');
      }
    };
    loadLogo();
    
    // Load quote configuration
    const loadQuoteConfig = async () => {
      try {
        const response = await api.getQuoteConfig();
        if (response.data) {
          setQuoteConfig(response.data);
        }
      } catch (e) {
        console.log('Could not load quote config');
      }
    };
    loadQuoteConfig();
  }, []);

  const commissionRate = 20;
  const infinitoCostRate = 20;

  const categories = useMemo(() => [...new Set(serviceCatalog.map(s => s.category))], [serviceCatalog]);

  const statusOptions = [
    { value: 'Pendiente', label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'Enviada', label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
    { value: 'Aprobada', label: 'Aprobada', color: 'bg-green-100 text-green-700' },
    { value: 'Rechazada', label: 'Rechazada', color: 'bg-red-100 text-red-700' },
    { value: 'Completada', label: 'Completada', color: 'bg-purple-100 text-purple-700' }
  ];

  const unitOptions = ['hour', 'session', 'page', 'ad', 'video', 'voiceover', 'post', 'release', 'spot', 'event', 'program', 'model', 'camera'];
  const formatOptions = ['Digital', 'Human', 'AI', 'News', 'Audio', 'TV Commercial', 'Live Program'];
  const categoryOptions = ['Creative Production', 'AI Production', 'Social Media', 'Press', 'Radio', 'Traditional TV', 'Digital TV'];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem('serviceCatalog', JSON.stringify(serviceCatalog));
  }, [serviceCatalog]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [evaluacionesRes, empresasRes, usersRes] = await Promise.all([
        api.getEvaluaciones(),
        api.getEmpresas(),
        api.getUsers()
      ]);
      setEvaluaciones(evaluacionesRes.data);
      setEmpresas(empresasRes.data);
      setUsers(usersRes.data);
      // Set default assigned user to current user
      if (!assignedUser && currentUser?.name) {
        setAssignedUser(currentUser.name);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter services by category
  const filteredServices = useMemo(() => {
    return selectedCategory === 'all' 
      ? serviceCatalog 
      : serviceCatalog.filter(s => s.category === selectedCategory);
  }, [selectedCategory, serviceCatalog]);

  // Service CRUD handlers
  const handleAddService = () => {
    const id = 'svc_' + Date.now();
    const service = { ...newService, id };
    setServiceCatalog([...serviceCatalog, service]);
    setShowAddServiceModal(false);
    setNewService({ name: '', detail: '', category: 'Creative Production', format: 'Digital', baseRate: 0, unit: 'hour', defaultHours: 1, defaultDays: 1, defaultWeeks: 1 });
  };

  const handleEditService = () => {
    setServiceCatalog(serviceCatalog.map(s => s.id === editingService.id ? editingService : s));
    setShowEditServiceModal(false);
    setEditingService(null);
  };

  const handleDeleteService = (id) => {
    if (window.confirm('¿Eliminar este servicio del catálogo?')) {
      setServiceCatalog(serviceCatalog.filter(s => s.id !== id));
    }
  };

  // Add service to cart
  const addToCart = (service) => {
    const existingItem = cart.find(item => item.serviceId === service.id);
    if (existingItem) {
      updateCartItem(service.id, { quantity: existingItem.quantity + 1 });
    } else {
      setCart([...cart, {
        serviceId: service.id,
        service: service,
        quantity: 1,
        hours: service.defaultHours || 1,
        days: service.defaultDays || 1,
        weeks: service.defaultWeeks || 1,
        units: 1,
        customRate: service.baseRate
      }]);
    }
  };

  const updateCartItem = (serviceId, updates) => {
    setCart(cart.map(item => item.serviceId === serviceId ? { ...item, ...updates } : item));
  };

  const removeFromCart = (serviceId) => {
    setCart(cart.filter(item => item.serviceId !== serviceId));
  };

  const calculateItemTotal = (item) => {
    const rate = item.customRate || item.service.baseRate;
    if (item.service.unit === 'hour') return rate * item.hours * item.days * item.weeks;
    if (['post', 'spot'].includes(item.service.unit)) return rate * item.days * item.weeks;
    if (item.service.unit === 'program') return rate * item.weeks;
    return rate * item.quantity;
  };

  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const infinitoCost = subtotal * (infinitoCostRate / 100);
    const salesCommission = subtotal * (commissionRate / 100);
    const labor = cart.reduce((sum, item) => item.service.laborPercent ? sum + (calculateItemTotal(item) * item.service.laborPercent / 100) : sum, 0);
    const mcStaff = cart.reduce((sum, item) => sum + (item.service.mcStaff || 0), 0);
    return { subtotal, infinitoCost, salesCommission, labor, mcStaff, total: subtotal };
  }, [cart]);

  const saveQuote = async () => {
    if (!selectedEmpresa || cart.length === 0) {
      alert('Selecciona una empresa y agrega servicios');
      return;
    }
    if (!assignedUser) {
      alert('Selecciona el usuario asignado a esta cotización');
      return;
    }
    const empresa = empresas.find(e => e.id === selectedEmpresa);
    const servicesDetail = cart.map(item => ({
      serviceId: item.serviceId, serviceName: item.service.name, detail: item.service.detail,
      category: item.service.category, quantity: item.quantity, hours: item.hours, 
      days: item.days, weeks: item.weeks, rate: item.customRate, total: calculateItemTotal(item)
    }));

    try {
      const evalData = {
        empresa_id: selectedEmpresa, empresa_name: empresa?.name || '',
        service_name: quoteName || `Cotización ${formatDatePR(new Date())}`,
        description: quoteNotes, services: servicesDetail, appraisal_value: cartTotals.subtotal,
        infinito_cost: cartTotals.infinitoCost, sales_commission: cartTotals.salesCommission,
        labor: cartTotals.labor, mc_staff: cartTotals.mcStaff, frequency: 'One Time Deal',
        monthly_value: 0, sold_by: assignedUser, assigned_to: assignedUser, 
        labor_done_by: '', status: 'Pendiente'
      };
      const response = await api.createEvaluacion(evalData, commissionRate);
      setEvaluaciones([...evaluaciones, response.data]);
      setCart([]); setSelectedEmpresa(''); setQuoteName(''); setQuoteNotes('');
      setAssignedUser(currentUser?.name || '');
      setShowPreviewModal(false); setActiveTab('historial');
      alert('Cotización guardada exitosamente');
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Error al guardar la cotización');
    }
  };

  const handleViewQuote = (evaluacion) => {
    setSelectedQuote(evaluacion);
    setEditingQuote(false);
    setShowQuoteDetailModal(true);
  };

  const handleUpdateQuote = async () => {
    try {
      const response = await api.updateEvaluacion(selectedQuote.id, selectedQuote, commissionRate);
      setEvaluaciones(evaluaciones.map(e => e.id === selectedQuote.id ? response.data : e));
      setShowQuoteDetailModal(false);
      setSelectedQuote(null);
      alert('Cotización actualizada');
    } catch (error) {
      console.error('Error updating quote:', error);
    }
  };

  const handleDeleteEvaluacion = async (evaluacionId) => {
    if (window.confirm('¿Está seguro de eliminar esta cotización?')) {
      try {
        await api.deleteEvaluacion(evaluacionId);
        setEvaluaciones(evaluaciones.filter(e => e.id !== evaluacionId));
      } catch (error) {
        console.error('Error deleting evaluacion:', error);
      }
    }
  };

  const handleUpdateStatus = async (evaluacionId, newStatus) => {
    try {
      const evaluacion = evaluaciones.find(e => e.id === evaluacionId);
      const response = await api.updateEvaluacion(evaluacionId, { ...evaluacion, status: newStatus }, commissionRate);
      setEvaluaciones(evaluaciones.map(e => e.id === evaluacionId ? response.data : e));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-700';

  // Helper to convert hex color to RGB array
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [90, 90, 90];
  };

  // Generate professional PDF quote matching the invoice template
  const generateQuotePDF = (quote) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Get colors from config
    const primaryRgb = hexToRgb(quoteConfig.primary_color);
    const accentRgb = hexToRgb(quoteConfig.accent_color);
    const balanceDueRgb = hexToRgb(quoteConfig.balance_due_color);
    
    // ===== HEADER SECTION =====
    // Header style based on template
    if (quoteConfig.header_style === 'boxed') {
      doc.setFillColor(...primaryRgb);
      doc.rect(0, 0, pageWidth, 45, 'F');
    } else if (quoteConfig.header_style === 'gradient') {
      // Simulated gradient with multiple rectangles
      for (let i = 0; i < 45; i++) {
        const ratio = i / 45;
        doc.setFillColor(
          primaryRgb[0] + (accentRgb[0] - primaryRgb[0]) * ratio,
          primaryRgb[1] + (accentRgb[1] - primaryRgb[1]) * ratio,
          primaryRgb[2] + (accentRgb[2] - primaryRgb[2]) * ratio
        );
        doc.rect(0, i, pageWidth, 1, 'F');
      }
    }
    
    // Logo position based on config
    const logoPositions = { left: 15, center: pageWidth/2 - 22.5, right: pageWidth - 60 };
    const logoX = logoPositions[quoteConfig.logo_position] || 15;
    
    // Add logo
    if (logoBase64 && quoteConfig.show_logo) {
      try {
        doc.addImage(logoBase64, 'PNG', logoX, 10, 45, 30);
      } catch (e) {
        console.log('Logo could not be added to PDF');
      }
    }
    
    // Company info - adjust position based on header style and logo position
    const isHeaderDark = quoteConfig.header_style === 'boxed' || quoteConfig.header_style === 'gradient';
    const companyInfoY = isHeaderDark ? 50 : 47;
    
    if (quoteConfig.show_company_info) {
      doc.setFontSize(9);
      doc.setTextColor(isHeaderDark && companyInfoY < 45 ? 255 : 100, isHeaderDark && companyInfoY < 45 ? 255 : 100, isHeaderDark && companyInfoY < 45 ? 255 : 100);
      doc.setFont('helvetica', 'normal');
      doc.text('www.infinitotelevision.com', 15, companyInfoY);
      doc.text('Puerto Rico', 15, companyInfoY + 5);
      doc.text('infinitotelevisionpr@gmail.com', 15, companyInfoY + 10);
      doc.text('(787) 210-5788', 15, companyInfoY + 15);
    }
    
    // Invoice/Quote details (right side, top)
    const detailsTextColor = isHeaderDark ? [255, 255, 255] : [60, 60, 60];
    doc.setFontSize(10);
    doc.setTextColor(...detailsTextColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Cotización No.:', pageWidth - 60, 15);
    doc.setFont('helvetica', 'normal');
    doc.text(quote.id?.slice(-8).toUpperCase() || 'N/A', pageWidth - 15, 15, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.text('Fecha:', pageWidth - 60, 22);
    doc.setFont('helvetica', 'normal');
    doc.text(quote.created_at ? formatDatePR(quote.created_at) : formatDatePR(new Date()), pageWidth - 15, 22, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.text('Válida hasta:', pageWidth - 60, 29);
    doc.setFont('helvetica', 'normal');
    const validDate = new Date();
    validDate.setDate(validDate.getDate() + 30);
    doc.text(formatDatePR(validDate), pageWidth - 15, 29, { align: 'right' });
    
    if (quote.assigned_to_name) {
      doc.setFont('helvetica', 'bold');
      doc.text('Vendedor:', pageWidth - 60, 36);
      doc.setFont('helvetica', 'normal');
      doc.text(quote.assigned_to_name, pageWidth - 15, 36, { align: 'right' });
    }
    
    // ===== CUSTOMER INFO SECTION =====
    let currentY = isHeaderDark ? 75 : 75;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);;
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Info:', 15, currentY);
    
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(quote.empresa_name || 'Cliente', 15, currentY + 8);
    
    // ===== PRODUCT/SERVICE TITLE =====
    currentY += 25;
    if (quote.service_name) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(quote.service_name, pageWidth - 30);
      doc.text(titleLines, 15, currentY);
      currentY += (titleLines.length * 6) + 10;
    }
    
    // ===== SERVICES TABLE =====
    const tableData = (quote.services || []).map(svc => {
      let quantityStr = '1';
      if (svc.hours && svc.days && svc.weeks && (svc.hours > 1 || svc.days > 1 || svc.weeks > 1)) {
        quantityStr = `${svc.hours}h x ${svc.days}d x ${svc.weeks}w`;
      } else if (svc.quantity && svc.quantity > 1) {
        quantityStr = String(svc.quantity);
      } else if (svc.hours && svc.weeks) {
        quantityStr = `${svc.hours}h x ${svc.weeks}w`;
      } else if (svc.quantity) {
        quantityStr = String(svc.quantity);
      }
      
      // Service name with description
      const serviceName = svc.serviceName || svc.service_name || 'Servicio';
      const serviceDetail = svc.detail || svc.description || '';
      const serviceDisplay = serviceDetail ? `${serviceName}\n${serviceDetail}` : serviceName;
      
      return [
        serviceDisplay,
        quantityStr,
        `$${(svc.rate || 0).toFixed(2)}`,
        `$${(svc.total || 0).toLocaleString()}`
      ];
    });

    autoTable(doc, {
      startY: currentY,
      head: [['Product or Service', 'Quantity', 'Price', 'Line Total']],
      body: tableData,
      theme: 'plain',
      headStyles: { 
        fillColor: [255, 255, 255], 
        textColor: primaryRgb, 
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 5,
        lineWidth: 0.5,
        lineColor: [200, 200, 200]
      },
      styles: { 
        fontSize: 9, 
        cellPadding: 5, 
        overflow: 'linebreak',
        lineColor: [230, 230, 230],
        lineWidth: 0.1
      },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: 15, right: 15 }
    });
    
    // ===== TOTALS SECTION =====
    currentY = doc.lastAutoTable.finalY + 10;
    
    // Subtotal
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal', pageWidth - 80, currentY);
    doc.text(`$${(quote.appraisal_value || 0).toLocaleString()}`, pageWidth - 15, currentY, { align: 'right' });
    
    // Taxes (optional)
    if (quoteConfig.show_taxes) {
      currentY += 8;
      doc.text('Taxes', pageWidth - 80, currentY);
      doc.text('$0.00', pageWidth - 15, currentY, { align: 'right' });
    }
    
    // Invoice Total
    currentY += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Total', pageWidth - 80, currentY);
    doc.text(`$${(quote.appraisal_value || 0).toLocaleString()}`, pageWidth - 15, currentY, { align: 'right' });
    
    // Amount Paid (optional)
    if (quoteConfig.show_amount_paid) {
      currentY += 8;
      doc.setFont('helvetica', 'normal');
      doc.text('Amount Paid', pageWidth - 80, currentY);
      doc.text('$0.00', pageWidth - 15, currentY, { align: 'right' });
    }
    
    // Balance Due (with configured background color)
    currentY += 10;
    doc.setFillColor(...balanceDueRgb);
    doc.rect(pageWidth - 100, currentY - 6, 85, 12, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryRgb);
    doc.text('Balance Due', pageWidth - 80, currentY);
    doc.text(`$${(quote.appraisal_value || 0).toLocaleString()}`, pageWidth - 15, currentY, { align: 'right' });
    
    // ===== NOTES SECTION =====
    currentY += 20;
    doc.setTextColor(0, 0, 0);
    
    // Quote-specific notes
    if (quote.description) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const splitNotes = doc.splitTextToSize(quote.description, pageWidth - 30);
      doc.text(splitNotes, 15, currentY);
      currentY += (splitNotes.length * 4) + 8;
    }
    
    // ===== PAYMENT INFO (from config) =====
    if (quoteConfig.payment_info) {
      // Check if we need more space
      const paymentLines = doc.splitTextToSize(quoteConfig.payment_info, pageWidth - 30);
      if (currentY + (paymentLines.length * 4) > pageHeight - 40) {
        doc.addPage();
        currentY = 20;
      }
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'normal');
      doc.text(paymentLines, 15, currentY);
      currentY += (paymentLines.length * 4) + 8;
    }
    
    // ===== TERMS (from config) =====
    if (quoteConfig.terms_text) {
      if (currentY > pageHeight - 30) {
        doc.addPage();
        currentY = 20;
      }
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.setFont('helvetica', 'italic');
      const splitTerms = doc.splitTextToSize(quoteConfig.terms_text, pageWidth - 30);
      doc.text(splitTerms, 15, currentY);
      currentY += (splitTerms.length * 4) + 5;
    }
    
    // ===== GLOBAL NOTE (from config) =====
    if (quoteConfig.global_note) {
      if (currentY > pageHeight - 25) {
        doc.addPage();
        currentY = 20;
      }
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      const splitGlobal = doc.splitTextToSize(quoteConfig.global_note, pageWidth - 30);
      doc.text(splitGlobal, 15, currentY);
    }
    
    return doc;
  };

  const handleDownloadPDF = (quote) => {
    try {
      const doc = generateQuotePDF(quote);
      const filename = `Cotizacion_${quote.empresa_name?.replace(/\s+/g, '_') || 'Cliente'}_${formatDatePR(new Date()).replace(/[\/\s,:]/g, '-')}.pdf`;
      
      // Try to save directly first
      doc.save(filename);
      
      // If that doesn't work, try opening in new window as fallback
      // const pdfBlob = doc.output('blob');
      // const pdfUrl = URL.createObjectURL(pdfBlob);
      // window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error al generar PDF: ' + error.message);
    }
  };

  const handleOpenSendModal = (quote) => {
    setSelectedQuote(quote);
    const empresa = empresas.find(e => e.id === quote.empresa_id);
    setSendEmpresaContacts(empresa?.contacts || []);
    setSendQuoteData({
      empresa_id: quote.empresa_id,
      contact_id: '',
      contact_email: empresa?.email || '',
      message: `Estimado cliente,\n\nAdjunto encontrará la cotización solicitada por un total de $${(quote.appraisal_value || 0).toLocaleString()}.\n\nQuedamos a su disposición para cualquier consulta.\n\nSaludos cordiales,\n${currentUser?.name || 'Infinito Televisión'}`
    });
    setShowSendQuoteModal(true);
  };

  const handleSendQuote = async () => {
    if (!sendQuoteData.contact_email) {
      alert('Seleccione un contacto o ingrese un email');
      return;
    }
    
    try {
      // Update quote status to "Enviada"
      await api.updateEvaluacion(selectedQuote.id, { ...selectedQuote, status: 'Enviada' }, commissionRate);
      
      // Generate PDF blob (in a real scenario, this would be sent via email API)
      const doc = generateQuotePDF(selectedQuote);
      const pdfBlob = doc.output('blob');
      
      // Log the email send (email service is mocked)
      await api.sendEmail({
        template_id: null,
        empresa_id: sendQuoteData.empresa_id,
        empresa_name: selectedQuote.empresa_name,
        contact_id: sendQuoteData.contact_id || null,
        contact_name: sendEmpresaContacts.find(c => c.id === sendQuoteData.contact_id)?.name || null,
        contact_email: sendQuoteData.contact_email,
        sender_id: currentUser?.id || 'system',
        sender_name: currentUser?.name || 'Infinito Televisión',
        sender_email: currentUser?.email || 'info@infinitotelevision.com',
        subject: `Cotización - ${selectedQuote.service_name || 'Infinito Televisión'}`,
        body: sendQuoteData.message
      });
      
      // Update local state
      setEvaluaciones(evaluaciones.map(e => e.id === selectedQuote.id ? { ...e, status: 'Enviada' } : e));
      setShowSendQuoteModal(false);
      setSelectedQuote(null);
      
      // Also download the PDF
      handleDownloadPDF(selectedQuote);
      
      alert(`Cotización enviada a ${sendQuoteData.contact_email}`);
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Error al enviar cotización');
    }
  };

  const handleContactChange = (contactId) => {
    const contact = sendEmpresaContacts.find(c => c.id === contactId);
    setSendQuoteData({
      ...sendQuoteData,
      contact_id: contactId,
      contact_email: contact?.email || ''
    });
  };

  // Update service in quote (for editing values)
  const handleUpdateQuoteService = (serviceIndex, field, value) => {
    const updatedServices = [...(selectedQuote.services || [])];
    updatedServices[serviceIndex] = {
      ...updatedServices[serviceIndex],
      [field]: parseFloat(value) || 0
    };
    // Recalculate total for the service
    const svc = updatedServices[serviceIndex];
    if (svc.hours) {
      updatedServices[serviceIndex].total = (svc.rate || 0) * (svc.hours || 1) * (svc.days || 1) * (svc.weeks || 1);
    } else {
      updatedServices[serviceIndex].total = (svc.rate || 0) * (svc.quantity || 1);
    }
    // Recalculate quote total
    const newTotal = updatedServices.reduce((sum, s) => sum + (s.total || 0), 0);
    setSelectedQuote({
      ...selectedQuote,
      services: updatedServices,
      appraisal_value: newTotal,
      sales_commission: newTotal * (commissionRate / 100),
      infinito_cost: newTotal * (infinitoCostRate / 100)
    });
  };

  const filteredEvaluaciones = evaluaciones.filter(e =>
    e.empresa_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.service_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalQuoteValue = evaluaciones.reduce((sum, e) => sum + (e.appraisal_value || 0), 0);
  const totalCommissions = evaluaciones.reduce((sum, e) => sum + (e.sales_commission || e.potential_commission || 0), 0);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="evaluaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Evaluaciones de Negocios Únicos</h1>
          <p className="text-sm text-gray-500">Cotizador de servicios de producción y publicidad</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setShowQuoteConfigModal(true)}
          className="flex items-center gap-2"
          data-testid="quote-config-btn"
        >
          <Settings size={16} />
          Configurar Cotizaciones
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cotizaciones</p>
              <p className="text-xl font-bold text-gray-800">{evaluaciones.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Valor Total</p>
              <p className="text-xl font-bold text-gray-800">${totalQuoteValue.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-[#D4A843]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Comisiones ({commissionRate}%)</p>
              <p className="text-xl font-bold text-[#D4A843]">${totalCommissions.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <ShoppingCart size={20} className="text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En Carrito</p>
              <p className="text-xl font-bold text-purple-600">{cart.length} servicios</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="cotizador" className="flex items-center gap-2">
            <Calculator size={16} /> Cotizador
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center gap-2">
            <FileText size={16} /> Historial
          </TabsTrigger>
          <TabsTrigger value="catalogo" className="flex items-center gap-2">
            <Eye size={16} /> Catálogo de Precios
          </TabsTrigger>
        </TabsList>

        {/* Cotizador Tab */}
        <TabsContent value="cotizador" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Services Catalog */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Servicios Disponibles</CardTitle>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-48"><SelectValue placeholder="Categoría" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[500px] overflow-y-auto">
                    {filteredServices.map((service) => {
                      const IconComponent = getIconForCategory(service.category);
                      const inCart = cart.find(item => item.serviceId === service.id);
                      return (
                        <div key={service.id} className={`p-4 border-b last:border-0 hover:bg-gray-50 transition-colors ${inCart ? 'bg-green-50' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                service.format === 'AI' ? 'bg-purple-100' : service.format === 'Digital' ? 'bg-blue-100' : service.format === 'Human' ? 'bg-amber-100' : 'bg-gray-100'
                              }`}>
                                <IconComponent size={20} className={service.format === 'AI' ? 'text-purple-600' : service.format === 'Digital' ? 'text-blue-600' : service.format === 'Human' ? 'text-amber-600' : 'text-gray-600'} />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-800">{service.name}</h4>
                                <p className="text-sm text-gray-500">{service.detail}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">{service.category}</Badge>
                                  <Badge variant="outline" className="text-xs">{service.format}</Badge>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-[#D4A843]">${service.baseRate.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">por {service.unit}</p>
                              <Button size="sm" onClick={() => addToCart(service)} className={inCart ? 'bg-green-600 hover:bg-green-700 mt-2' : 'bg-[#D4A843] hover:bg-[#c49a3a] mt-2'}>
                                {inCart ? <Check size={14} className="mr-1" /> : <Plus size={14} className="mr-1" />}
                                {inCart ? 'Agregado' : 'Agregar'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cart */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2"><ShoppingCart size={20} /> Cotización</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empresa *</label>
                    <EmpresaSearchSelect
                      empresas={empresas}
                      value={selectedEmpresa}
                      onChange={setSelectedEmpresa}
                      placeholder="Buscar empresa..."
                      data-testid="evaluacion-empresa-search"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a *</label>
                    <Select value={assignedUser} onValueChange={setAssignedUser}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar usuario" /></SelectTrigger>
                      <SelectContent>
                        {users.map(user => <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Comisiones y seguimiento</p>
                  </div>

                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {cart.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">Agrega servicios desde el catálogo</p>
                    ) : cart.map((item) => (
                      <div key={item.serviceId} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-sm text-gray-800">{item.service.name}</p>
                            <p className="text-xs text-gray-500">${item.customRate}/{item.service.unit}</p>
                          </div>
                          <button onClick={() => removeFromCart(item.serviceId)} className="p-1 hover:bg-red-100 rounded">
                            <X size={14} className="text-red-500" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {item.service.unit === 'hour' && (
                            <>
                              <div><label className="text-xs text-gray-500">Horas</label><Input type="number" value={item.hours} onChange={(e) => updateCartItem(item.serviceId, { hours: parseFloat(e.target.value) || 1 })} className="h-8 text-sm" min="1" /></div>
                              <div><label className="text-xs text-gray-500">Días</label><Input type="number" value={item.days} onChange={(e) => updateCartItem(item.serviceId, { days: parseFloat(e.target.value) || 1 })} className="h-8 text-sm" min="1" /></div>
                              <div><label className="text-xs text-gray-500">Semanas</label><Input type="number" value={item.weeks} onChange={(e) => updateCartItem(item.serviceId, { weeks: parseFloat(e.target.value) || 1 })} className="h-8 text-sm" min="1" /></div>
                            </>
                          )}
                          {['post', 'spot'].includes(item.service.unit) && (
                            <>
                              <div><label className="text-xs text-gray-500">Días</label><Input type="number" value={item.days} onChange={(e) => updateCartItem(item.serviceId, { days: parseFloat(e.target.value) || 1 })} className="h-8 text-sm" min="1" /></div>
                              <div><label className="text-xs text-gray-500">Semanas</label><Input type="number" value={item.weeks} onChange={(e) => updateCartItem(item.serviceId, { weeks: parseFloat(e.target.value) || 1 })} className="h-8 text-sm" min="1" /></div>
                            </>
                          )}
                          {!['hour', 'post', 'spot', 'program'].includes(item.service.unit) && (
                            <div className="col-span-3"><label className="text-xs text-gray-500">Cantidad</label><Input type="number" value={item.quantity} onChange={(e) => updateCartItem(item.serviceId, { quantity: parseFloat(e.target.value) || 1 })} className="h-8 text-sm" min="1" /></div>
                          )}
                        </div>
                        <div className="text-right mt-2"><p className="text-sm font-bold text-[#D4A843]">${calculateItemTotal(item).toLocaleString()}</p></div>
                      </div>
                    ))}
                  </div>

                  {cart.length > 0 && (
                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal:</span><span className="font-medium">${cartTotals.subtotal.toLocaleString()}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-600">Infinito Cost ({infinitoCostRate}%):</span><span className="text-blue-600">${cartTotals.infinitoCost.toLocaleString()}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-600">Comisión Ventas ({commissionRate}%):</span><span className="text-[#D4A843]">${cartTotals.salesCommission.toLocaleString()}</span></div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span className="text-[#D4A843]">${cartTotals.total.toLocaleString()}</span></div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Button className="w-full bg-[#D4A843] hover:bg-[#c49a3a]" disabled={cart.length === 0 || !selectedEmpresa} onClick={() => setShowPreviewModal(true)}>
                      <Eye size={16} className="mr-2" /> Vista Previa y Guardar
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => setCart([])} disabled={cart.length === 0}>
                      <Trash2 size={16} className="mr-2" /> Limpiar Carrito
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Historial Tab */}
        <TabsContent value="historial" className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input placeholder="Buscar por empresa o servicio..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#5a5a5a] text-white">
                      <th className="text-left py-3 px-4 text-sm font-medium">Empresa</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Cotización</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Valor Total</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Comisión</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Estado</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvaluaciones.map((evaluacion) => (
                      <tr key={evaluacion.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4"><div className="flex items-center space-x-2"><Building2 size={14} className="text-gray-400" /><span className="text-sm font-medium text-gray-700">{evaluacion.empresa_name}</span></div></td>
                        <td className="py-3 px-4 text-sm text-gray-600">{evaluacion.service_name}</td>
                        <td className="py-3 px-4 text-sm text-[#D4A843] font-semibold">${(evaluacion.appraisal_value || 0).toLocaleString()}</td>
                        <td className="py-3 px-4 text-sm text-[#D4A843] font-semibold">${(evaluacion.sales_commission || evaluacion.potential_commission || 0).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          <Select value={evaluacion.status} onValueChange={(value) => handleUpdateStatus(evaluacion.id, value)}>
                            <SelectTrigger className={`w-32 h-8 ${getStatusColor(evaluacion.status)}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{statusOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">{evaluacion.created_at ? formatDatePR(evaluacion.created_at) : '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-1">
                            <button onClick={() => handleViewQuote(evaluacion)} className="p-2 hover:bg-blue-50 rounded-lg" title="Ver detalles"><Eye size={16} className="text-blue-500" /></button>
                            <button onClick={() => handleDownloadPDF(evaluacion)} className="p-2 hover:bg-green-50 rounded-lg" title="Descargar PDF"><Download size={16} className="text-green-600" /></button>
                            <button onClick={() => handleOpenSendModal(evaluacion)} className="p-2 hover:bg-purple-50 rounded-lg" title="Enviar al cliente"><Send size={16} className="text-purple-600" /></button>
                            <button onClick={() => handleDeleteEvaluacion(evaluacion.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Eliminar"><Trash2 size={16} className="text-red-500" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredEvaluaciones.length === 0 && <tr><td colSpan="7" className="py-8 text-center text-gray-500">No se encontraron cotizaciones</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Catalogo Tab */}
        <TabsContent value="catalogo" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Lista de Precios por Servicios</CardTitle>
              <Button onClick={() => setShowAddServiceModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
                <Plus size={16} className="mr-2" /> Agregar Servicio
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#5a5a5a] text-white">
                      <th className="text-left py-3 px-4 text-sm font-medium">Servicio</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Detalle</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Formato</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Categoría</th>
                      <th className="text-right py-3 px-4 text-sm font-medium">Tarifa</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Unidad</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceCatalog.map((service) => {
                      const IconComponent = getIconForCategory(service.category);
                      return (
                        <tr key={service.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4"><div className="flex items-center gap-2"><IconComponent size={16} className="text-gray-400" /><span className="text-sm font-medium text-gray-800">{service.name}</span></div></td>
                          <td className="py-3 px-4 text-sm text-gray-600">{service.detail}</td>
                          <td className="py-3 px-4"><Badge variant="outline" className="text-xs">{service.format}</Badge></td>
                          <td className="py-3 px-4 text-sm text-gray-600">{service.category}</td>
                          <td className="py-3 px-4 text-right"><span className="text-[#D4A843] font-bold">${service.baseRate.toFixed(2)}</span></td>
                          <td className="py-3 px-4 text-sm text-gray-500">{service.unit}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-1">
                              <button onClick={() => { setEditingService({...service}); setShowEditServiceModal(true); }} className="p-1 hover:bg-gray-100 rounded"><Edit size={14} className="text-gray-500" /></button>
                              <button onClick={() => handleDeleteService(service.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-500" /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Vista Previa de Cotización</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Cotización</label><Input value={quoteName} onChange={(e) => setQuoteName(e.target.value)} placeholder={`Cotización ${formatDatePR(new Date())}`} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label><Input value={empresas.find(e => e.id === selectedEmpresa)?.name || ''} disabled /></div>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Notas</label><textarea value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" rows={2} placeholder="Notas adicionales..." /></div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100"><tr><th className="text-left py-2 px-3 text-sm">Servicio</th><th className="text-right py-2 px-3 text-sm">Cantidad</th><th className="text-right py-2 px-3 text-sm">Tarifa</th><th className="text-right py-2 px-3 text-sm">Total</th></tr></thead>
                <tbody>
                  {cart.map((item) => (
                    <tr key={item.serviceId} className="border-t">
                      <td className="py-2 px-3 text-sm">{item.service.name}</td>
                      <td className="py-2 px-3 text-sm text-right">{item.service.unit === 'hour' ? `${item.hours}h x ${item.days}d x ${item.weeks}w` : ['post', 'spot'].includes(item.service.unit) ? `${item.days}d x ${item.weeks}w` : item.quantity}</td>
                      <td className="py-2 px-3 text-sm text-right">${item.customRate}</td>
                      <td className="py-2 px-3 text-sm text-right font-medium">${calculateItemTotal(item).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t">
                  <tr><td colSpan="3" className="py-2 px-3 text-sm font-medium text-right">Subtotal:</td><td className="py-2 px-3 text-sm text-right font-bold">${cartTotals.subtotal.toLocaleString()}</td></tr>
                  <tr><td colSpan="3" className="py-2 px-3 text-sm text-right text-gray-600">Infinito Cost ({infinitoCostRate}%):</td><td className="py-2 px-3 text-sm text-right text-blue-600">${cartTotals.infinitoCost.toLocaleString()}</td></tr>
                  <tr><td colSpan="3" className="py-2 px-3 text-sm text-right text-gray-600">Comisión Ventas ({commissionRate}%):</td><td className="py-2 px-3 text-sm text-right text-[#D4A843]">${cartTotals.salesCommission.toLocaleString()}</td></tr>
                  <tr className="border-t"><td colSpan="3" className="py-3 px-3 text-lg font-bold text-right">TOTAL:</td><td className="py-3 px-3 text-lg text-right font-bold text-[#D4A843]">${cartTotals.total.toLocaleString()}</td></tr>
                </tfoot>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Cancelar</Button>
            <Button onClick={saveQuote} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Check size={16} className="mr-2" /> Guardar Cotización</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Service Modal */}
      <Dialog open={showAddServiceModal} onOpenChange={setShowAddServiceModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Agregar Servicio al Catálogo</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label><Input value={newService.name} onChange={(e) => setNewService({...newService, name: e.target.value})} placeholder="Nombre del servicio" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Detalle</label><Input value={newService.detail} onChange={(e) => setNewService({...newService, detail: e.target.value})} placeholder="Descripción" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label><Select value={newService.category} onValueChange={(v) => setNewService({...newService, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Formato</label><Select value={newService.format} onValueChange={(v) => setNewService({...newService, format: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{formatOptions.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Tarifa ($)</label><Input type="number" value={newService.baseRate} onChange={(e) => setNewService({...newService, baseRate: parseFloat(e.target.value) || 0})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label><Select value={newService.unit} onValueChange={(v) => setNewService({...newService, unit: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{unitOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddServiceModal(false)}>Cancelar</Button>
            <Button onClick={handleAddService} className="bg-[#D4A843] hover:bg-[#c49a3a]" disabled={!newService.name}><Plus size={16} className="mr-2" /> Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Service Modal */}
      <Dialog open={showEditServiceModal} onOpenChange={setShowEditServiceModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Servicio</DialogTitle></DialogHeader>
          {editingService && (
            <div className="space-y-4 py-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label><Input value={editingService.name} onChange={(e) => setEditingService({...editingService, name: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Detalle</label><Input value={editingService.detail} onChange={(e) => setEditingService({...editingService, detail: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label><Select value={editingService.category} onValueChange={(v) => setEditingService({...editingService, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Formato</label><Select value={editingService.format} onValueChange={(v) => setEditingService({...editingService, format: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{formatOptions.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Tarifa ($)</label><Input type="number" value={editingService.baseRate} onChange={(e) => setEditingService({...editingService, baseRate: parseFloat(e.target.value) || 0})} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label><Select value={editingService.unit} onValueChange={(v) => setEditingService({...editingService, unit: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{unitOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditServiceModal(false)}>Cancelar</Button>
            <Button onClick={handleEditService} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Save size={16} className="mr-2" /> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Detail/Edit Modal */}
      <Dialog open={showQuoteDetailModal} onOpenChange={setShowQuoteDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Detalles de Cotización</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(selectedQuote)}>
                  <Download size={14} className="mr-1" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleOpenSendModal(selectedQuote)} className="text-blue-600 border-blue-300">
                  <Send size={14} className="mr-1" /> Enviar
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditingQuote(!editingQuote)}>
                  <Edit size={14} className="mr-1" /> {editingQuote ? 'Cancelar' : 'Editar'}
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label><Input value={selectedQuote.empresa_name} disabled /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Cotización</label><Input value={selectedQuote.service_name} disabled={!editingQuote} onChange={(e) => setSelectedQuote({...selectedQuote, service_name: e.target.value})} /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label><textarea value={selectedQuote.description || ''} disabled={!editingQuote} onChange={(e) => setSelectedQuote({...selectedQuote, description: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50" rows={2} /></div>
              
              {/* Services List - Editable */}
              {selectedQuote.services && selectedQuote.services.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left py-2 px-3 text-sm">Servicio</th>
                        <th className="text-right py-2 px-3 text-sm">Cant.</th>
                        <th className="text-right py-2 px-3 text-sm">Tarifa</th>
                        <th className="text-right py-2 px-3 text-sm">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuote.services.map((svc, idx) => {
                        // Calculate display quantity
                        let quantityDisplay = '';
                        if (svc.hours && svc.days && svc.weeks && (svc.hours > 1 || svc.days > 1 || svc.weeks > 1)) {
                          quantityDisplay = `${svc.hours}h x ${svc.days}d x ${svc.weeks}w`;
                        } else if (svc.quantity && svc.quantity > 1) {
                          quantityDisplay = String(svc.quantity);
                        } else if (svc.hours && svc.weeks && svc.weeks > 1) {
                          quantityDisplay = `${svc.hours}h x ${svc.weeks}w`;
                        } else {
                          quantityDisplay = String(svc.quantity || 1);
                        }
                        
                        return (
                        <tr key={idx} className="border-t">
                          <td className="py-2 px-3 text-sm">{svc.serviceName}</td>
                          <td className="py-2 px-3 text-sm text-right">
                            {editingQuote ? (
                              <Input 
                                type="number" 
                                value={svc.quantity || 1} 
                                onChange={(e) => handleUpdateQuoteService(idx, 'quantity', e.target.value)}
                                className="w-16 text-right h-8"
                              />
                            ) : (
                              quantityDisplay
                            )}
                          </td>
                          <td className="py-2 px-3 text-sm text-right">
                            {editingQuote ? (
                              <Input 
                                type="number" 
                                value={svc.rate || 0} 
                                onChange={(e) => handleUpdateQuoteService(idx, 'rate', e.target.value)}
                                className="w-20 text-right h-8"
                              />
                            ) : (
                              `$${svc.rate}`
                            )}
                          </td>
                          <td className="py-2 px-3 text-sm text-right font-medium text-[#D4A843]">
                            ${svc.total?.toLocaleString()}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t">
                      <tr>
                        <td colSpan="3" className="py-3 px-3 text-lg font-bold text-right">TOTAL:</td>
                        <td className="py-3 px-3 text-lg text-right font-bold text-[#D4A843]">
                          ${(selectedQuote.appraisal_value || 0).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* If no services, show editable total */}
              {(!selectedQuote.services || selectedQuote.services.length === 0) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Total</label>
                    <Input 
                      type="number" 
                      value={selectedQuote.appraisal_value || 0} 
                      disabled={!editingQuote} 
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value) || 0;
                        setSelectedQuote({
                          ...selectedQuote, 
                          appraisal_value: newValue,
                          sales_commission: newValue * (commissionRate / 100),
                          infinito_cost: newValue * (infinitoCostRate / 100)
                        });
                      }} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Comisión (interno)</label>
                    <Input value={`$${(selectedQuote.sales_commission || selectedQuote.potential_commission || 0).toLocaleString()}`} disabled className="text-gray-500" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <Select value={selectedQuote.status} disabled={!editingQuote} onValueChange={(v) => setSelectedQuote({...selectedQuote, status: v})}>
                    <SelectTrigger className={getStatusColor(selectedQuote.status)}><SelectValue /></SelectTrigger>
                    <SelectContent>{statusOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label><Input value={selectedQuote.created_at ? formatDatePR(selectedQuote.created_at) : '-'} disabled /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteDetailModal(false)}>Cerrar</Button>
            {editingQuote && <Button onClick={handleUpdateQuote} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Save size={16} className="mr-2" /> Guardar Cambios</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Quote Modal */}
      <Dialog open={showSendQuoteModal} onOpenChange={setShowSendQuoteModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} className="text-blue-600" />
              Enviar Cotización al Cliente
            </DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium">{selectedQuote.service_name}</p>
                <p className="text-lg font-bold text-[#D4A843]">${(selectedQuote.appraisal_value || 0).toLocaleString()}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <Input value={selectedQuote.empresa_name} disabled />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
                <Select 
                  value={sendQuoteData.contact_id || "direct"} 
                  onValueChange={(v) => {
                    if (v === "direct") {
                      setSendQuoteData({ ...sendQuoteData, contact_id: '', contact_email: '' });
                    } else {
                      handleContactChange(v);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar contacto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Enviar a email directo</SelectItem>
                    {sendEmpresaContacts.map(contact => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} {contact.email && `(${contact.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email del destinatario</label>
                <Input 
                  type="email"
                  value={sendQuoteData.contact_email} 
                  onChange={(e) => setSendQuoteData({ ...sendQuoteData, contact_email: e.target.value })}
                  placeholder="email@ejemplo.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje</label>
                <textarea 
                  value={sendQuoteData.message} 
                  onChange={(e) => setSendQuoteData({ ...sendQuoteData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-[120px]"
                  placeholder="Mensaje para el cliente..."
                />
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium">📎 Se adjuntará el PDF de la cotización</p>
                <p className="text-xs mt-1">El PDF incluye solo los servicios y el total (sin desglose de comisiones)</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendQuoteModal(false)}>Cancelar</Button>
            <Button 
              onClick={handleSendQuote} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!sendQuoteData.contact_email}
            >
              <Send size={16} className="mr-2" /> Enviar Cotización
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Configuration Modal */}
      <Dialog open={showQuoteConfigModal} onOpenChange={setShowQuoteConfigModal}>
        <DialogContent className="max-w-6xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={20} />
              Configuración de Cotizaciones PDF
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-6 flex-1 overflow-hidden">
            {/* Left Panel - Configuration Options */}
            <div className="w-1/2 overflow-y-auto pr-4 space-y-5">
            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccionar Plantilla
              </label>
              <div className="grid grid-cols-2 gap-2">
                {templates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => setQuoteConfig({
                      ...quoteConfig, 
                      template: template.id,
                      ...template.defaults
                    })}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      quoteConfig.template === template.id 
                        ? 'border-[#D4A843] bg-amber-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl text-center">{template.preview}</div>
                    <div className="text-xs font-medium text-center">{template.name}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Color Customization */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Color Primario</label>
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    value={quoteConfig.primary_color}
                    onChange={(e) => setQuoteConfig({...quoteConfig, primary_color: e.target.value})}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input
                    value={quoteConfig.primary_color}
                    onChange={(e) => setQuoteConfig({...quoteConfig, primary_color: e.target.value})}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Color Acento</label>
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    value={quoteConfig.accent_color}
                    onChange={(e) => setQuoteConfig({...quoteConfig, accent_color: e.target.value})}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input
                    value={quoteConfig.accent_color}
                    onChange={(e) => setQuoteConfig({...quoteConfig, accent_color: e.target.value})}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Balance Due</label>
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    value={quoteConfig.balance_due_color}
                    onChange={(e) => setQuoteConfig({...quoteConfig, balance_due_color: e.target.value})}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input
                    value={quoteConfig.balance_due_color}
                    onChange={(e) => setQuoteConfig({...quoteConfig, balance_due_color: e.target.value})}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Layout Options */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Posición Logo</label>
                <Select 
                  value={quoteConfig.logo_position} 
                  onValueChange={(value) => setQuoteConfig({...quoteConfig, logo_position: value})}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Izquierda</SelectItem>
                    <SelectItem value="center">Centro</SelectItem>
                    <SelectItem value="right">Derecha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Estilo Encabezado</label>
                <Select 
                  value={quoteConfig.header_style} 
                  onValueChange={(value) => setQuoteConfig({...quoteConfig, header_style: value})}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clean">Limpio (sin fondo)</SelectItem>
                    <SelectItem value="boxed">Con fondo sólido</SelectItem>
                    <SelectItem value="gradient">Con degradado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Display Options */}
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={quoteConfig.show_logo}
                  onChange={(e) => setQuoteConfig({...quoteConfig, show_logo: e.target.checked})}
                  className="rounded w-4 h-4"
                />
                <span className="text-xs">Logo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={quoteConfig.show_company_info}
                  onChange={(e) => setQuoteConfig({...quoteConfig, show_company_info: e.target.checked})}
                  className="rounded w-4 h-4"
                />
                <span className="text-xs">Info Empresa</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={quoteConfig.show_taxes}
                  onChange={(e) => setQuoteConfig({...quoteConfig, show_taxes: e.target.checked})}
                  className="rounded w-4 h-4"
                />
                <span className="text-xs">Taxes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={quoteConfig.show_amount_paid}
                  onChange={(e) => setQuoteConfig({...quoteConfig, show_amount_paid: e.target.checked})}
                  className="rounded w-4 h-4"
                />
                <span className="text-xs">Amount Paid</span>
              </label>
            </div>

            <hr className="border-gray-200" />

            {/* Text Content Section - Compact */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-gray-700">Contenido de Texto</h3>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nota Global</label>
                <textarea
                  value={quoteConfig.global_note}
                  onChange={(e) => setQuoteConfig({...quoteConfig, global_note: e.target.value})}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                  rows={2}
                  placeholder="Ej: Muchas gracias por su negocio..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Información de Pago</label>
                <textarea
                  value={quoteConfig.payment_info}
                  onChange={(e) => setQuoteConfig({...quoteConfig, payment_info: e.target.value})}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                  rows={3}
                  placeholder="Ej: Puede realizar el pago con tarjeta..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Términos</label>
                <textarea
                  value={quoteConfig.terms_text}
                  onChange={(e) => setQuoteConfig({...quoteConfig, terms_text: e.target.value})}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                  rows={2}
                  placeholder="Ej: Términos de pago 15 días..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Pie de Página</label>
                <Input
                  value={quoteConfig.footer_text}
                  onChange={(e) => setQuoteConfig({...quoteConfig, footer_text: e.target.value})}
                  placeholder="Infinito Televisión..."
                  className="h-8 text-xs"
                />
              </div>
            </div>
            </div>
            
            {/* Right Panel - Live Preview */}
            <div className="w-1/2 border-l pl-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Vista Previa</h3>
              <div 
                className="bg-white border rounded-lg shadow-inner overflow-auto"
                style={{ height: 'calc(100% - 30px)' }}
              >
                {/* PDF Preview - Scaled down */}
                <div 
                  className="p-4 origin-top-left"
                  style={{ 
                    transform: 'scale(0.6)', 
                    width: '166%',
                    minHeight: '800px',
                    backgroundColor: 'white'
                  }}
                >
                  {/* Header */}
                  <div 
                    className="relative mb-4 p-4 rounded"
                    style={{ 
                      backgroundColor: quoteConfig.header_style !== 'clean' ? quoteConfig.primary_color : 'transparent',
                      background: quoteConfig.header_style === 'gradient' 
                        ? `linear-gradient(to right, ${quoteConfig.primary_color}, ${quoteConfig.accent_color})`
                        : undefined
                    }}
                  >
                    <div className="flex justify-between items-start">
                      {/* Logo area */}
                      <div className={`flex items-center ${quoteConfig.logo_position === 'center' ? 'mx-auto' : quoteConfig.logo_position === 'right' ? 'ml-auto' : ''}`}>
                        {quoteConfig.show_logo && (
                          <div className="w-24 h-16 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                            LOGO
                          </div>
                        )}
                      </div>
                      {/* Quote info */}
                      <div className={`text-right text-xs ${quoteConfig.header_style !== 'clean' ? 'text-white' : 'text-gray-600'}`}>
                        <div><strong>Cotización No.:</strong> ABC12345</div>
                        <div><strong>Fecha:</strong> 24/02/2026</div>
                        <div><strong>Válida hasta:</strong> 26/03/2026</div>
                        <div><strong>Vendedor:</strong> Julio Del Río</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Company Info */}
                  {quoteConfig.show_company_info && (
                    <div className="text-xs text-gray-500 mb-4">
                      <div>www.infinitotelevision.com</div>
                      <div>Puerto Rico</div>
                      <div>infinitotelevisionpr@gmail.com</div>
                      <div>(787) 210-5788</div>
                    </div>
                  )}
                  
                  {/* Customer Info */}
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 font-bold">Customer Info:</div>
                    <div className="font-bold">Empresa Demo S.A.</div>
                  </div>
                  
                  {/* Services Table */}
                  <table className="w-full text-xs mb-4 border-collapse">
                    <thead>
                      <tr className="border-b" style={{ color: quoteConfig.primary_color }}>
                        <th className="text-left py-2">Product or Service</th>
                        <th className="text-center py-2">Qty</th>
                        <th className="text-right py-2">Price</th>
                        <th className="text-right py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2">
                          <div>Producción de Video</div>
                          <div className="text-gray-400 text-xs">Grabación y edición profesional</div>
                        </td>
                        <td className="text-center py-2">1</td>
                        <td className="text-right py-2">$1,500.00</td>
                        <td className="text-right py-2">$1,500.00</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2">Diseño Gráfico</td>
                        <td className="text-center py-2">2</td>
                        <td className="text-right py-2">$350.00</td>
                        <td className="text-right py-2">$700.00</td>
                      </tr>
                    </tbody>
                  </table>
                  
                  {/* Totals */}
                  <div className="text-right text-xs space-y-1 mb-4">
                    <div className="flex justify-end gap-8">
                      <span>Subtotal</span>
                      <span>$2,200.00</span>
                    </div>
                    {quoteConfig.show_taxes && (
                      <div className="flex justify-end gap-8">
                        <span>Taxes</span>
                        <span>$0.00</span>
                      </div>
                    )}
                    <div className="flex justify-end gap-8 font-bold">
                      <span>Invoice Total</span>
                      <span>$2,200.00</span>
                    </div>
                    {quoteConfig.show_amount_paid && (
                      <div className="flex justify-end gap-8">
                        <span>Amount Paid</span>
                        <span>$0.00</span>
                      </div>
                    )}
                    <div 
                      className="flex justify-end gap-8 font-bold px-3 py-1 rounded mt-2"
                      style={{ 
                        backgroundColor: quoteConfig.balance_due_color,
                        color: quoteConfig.primary_color
                      }}
                    >
                      <span>Balance Due</span>
                      <span>$2,200.00</span>
                    </div>
                  </div>
                  
                  {/* Payment Info */}
                  {quoteConfig.payment_info && (
                    <div className="text-xs text-gray-600 mb-3 whitespace-pre-line">
                      {quoteConfig.payment_info.substring(0, 150)}...
                    </div>
                  )}
                  
                  {/* Terms */}
                  {quoteConfig.terms_text && (
                    <div className="text-xs text-gray-500 italic mb-3">
                      {quoteConfig.terms_text}
                    </div>
                  )}
                  
                  {/* Global Note */}
                  {quoteConfig.global_note && (
                    <div className="text-xs text-gray-400 italic mb-4">
                      {quoteConfig.global_note}
                    </div>
                  )}
                  
                  {/* Footer */}
                  <div className="border-t pt-2 text-center text-xs text-gray-400" style={{ borderColor: quoteConfig.accent_color }}>
                    {quoteConfig.footer_text}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-between border-t pt-4">
            <Button variant="outline" onClick={() => setShowQuoteConfigModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={async () => {
                try {
                  await api.updateQuoteConfig(quoteConfig);
                  alert('Configuración guardada correctamente');
                  setShowQuoteConfigModal(false);
                } catch (error) {
                  alert('Error al guardar: ' + (error.response?.data?.detail || error.message));
                }
              }}
              className="bg-[#D4A843] hover:bg-[#c49a3a]"
            >
              <Save size={16} className="mr-2" />
              Guardar Configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvaluacionesPage;
